import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Plus, Trash2, Edit, Save, Eye, EyeOff, GripVertical, Copy,
  ChevronDown, ChevronUp, Settings, Image, Type, Layout, 
  Columns, Video, Code, HelpCircle, Mail, Minus, RotateCcw,
  Clock, Calendar, Check, X, ArrowLeft, FileText, Globe,
  Sparkles, Loader2, History, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tropical theme colors
const colors = {
  green: { primary: "#00A86B", light: "#D0F0C0", dark: "#006B3C" },
  mango: { primary: "#FF9F1C", light: "#FFBF69" },
  plum: { primary: "#9B59B6", light: "#D7BDE2" }
};

// Block type icons
const BLOCK_ICONS = {
  hero: "🦸",
  text: "📝",
  image: "🖼️",
  cta: "🔘",
  features: "⭐",
  testimonials: "💬",
  gallery: "🖼️",
  video: "🎬",
  two_column: "▯▯",
  three_column: "▯▯▯",
  cards: "🃏",
  faq: "❓",
  spacer: "↕️",
  divider: "➖",
  html: "🧩",
  contact_form: "📧"
};

// Rich Text Editor Component (Simple version)
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = DOMPurify.sanitize(value || '');
    }
  }, [value]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b flex-wrap">
        <button 
          type="button"
          onClick={() => execCommand('bold')} 
          className="p-1.5 hover:bg-gray-200 rounded font-bold"
          title="Bold"
        >
          B
        </button>
        <button 
          type="button"
          onClick={() => execCommand('italic')} 
          className="p-1.5 hover:bg-gray-200 rounded italic"
          title="Italic"
        >
          I
        </button>
        <button 
          type="button"
          onClick={() => execCommand('underline')} 
          className="p-1.5 hover:bg-gray-200 rounded underline"
          title="Underline"
        >
          U
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button 
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')} 
          className="p-1.5 hover:bg-gray-200 rounded text-sm font-bold"
          title="Heading"
        >
          H2
        </button>
        <button 
          type="button"
          onClick={() => execCommand('formatBlock', '<h3>')} 
          className="p-1.5 hover:bg-gray-200 rounded text-sm font-bold"
          title="Subheading"
        >
          H3
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button 
          type="button"
          onClick={() => execCommand('insertUnorderedList')} 
          className="p-1.5 hover:bg-gray-200 rounded"
          title="Bullet List"
        >
          •
        </button>
        <button 
          type="button"
          onClick={() => execCommand('insertOrderedList')} 
          className="p-1.5 hover:bg-gray-200 rounded"
          title="Numbered List"
        >
          1.
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button 
          type="button"
          onClick={() => {
            const url = prompt('Enter link URL:');
            if (url) execCommand('createLink', url);
          }} 
          className="p-1.5 hover:bg-gray-200 rounded text-blue-600"
          title="Add Link"
        >
          🔗
        </button>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[150px] p-3 focus:outline-none prose prose-sm max-w-none"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

// Color Picker Component
const ColorPicker = ({ value, onChange, label }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="flex items-center gap-2 mt-1">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer"
      />
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#00A86B"
        className="flex-1 rounded-lg"
      />
    </div>
  </div>
);

// Block Editor Component
const BlockEditor = ({ block, blockTypes, onChange, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showStyleEditor, setShowStyleEditor] = useState(false);
  const blockDef = blockTypes[block.type] || {};

  const updateContent = (key, value) => {
    onChange({
      ...block,
      content: { ...block.content, [key]: value }
    });
  };

  const updateStyle = (key, value) => {
    onChange({
      ...block,
      styles: { ...block.styles, [key]: value }
    });
  };

  const renderContentEditor = () => {
    switch (block.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <Label>Headline</Label>
              <Input
                value={block.content.headline || ''}
                onChange={(e) => updateContent('headline', e.target.value)}
                placeholder="Welcome to ROOTSPACE"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Subheadline</Label>
              <Input
                value={block.content.subheadline || ''}
                onChange={(e) => updateContent('subheadline', e.target.value)}
                placeholder="Plan your dream garden"
                className="mt-1 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input
                  value={block.content.cta_text || ''}
                  onChange={(e) => updateContent('cta_text', e.target.value)}
                  placeholder="Get Started"
                  className="mt-1 rounded-lg"
                />
              </div>
              <div>
                <Label>Button Link</Label>
                <Input
                  value={block.content.cta_link || ''}
                  onChange={(e) => updateContent('cta_link', e.target.value)}
                  placeholder="/design"
                  className="mt-1 rounded-lg"
                />
              </div>
            </div>
            <div>
              <Label>Background Image URL</Label>
              <Input
                value={block.content.background_image || ''}
                onChange={(e) => updateContent('background_image', e.target.value)}
                placeholder="https://..."
                className="mt-1 rounded-lg"
              />
            </div>
            <ColorPicker
              label="Background Color"
              value={block.content.background_color}
              onChange={(v) => updateContent('background_color', v)}
            />
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <Label>Content</Label>
              <RichTextEditor
                value={block.content.content || ''}
                onChange={(v) => updateContent('content', v)}
                placeholder="Enter your text here..."
              />
            </div>
            <div>
              <Label>Alignment</Label>
              <select
                value={block.content.alignment || 'left'}
                onChange={(e) => updateContent('alignment', e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <Label>Image URL</Label>
              <Input
                value={block.content.src || ''}
                onChange={(e) => updateContent('src', e.target.value)}
                placeholder="https://..."
                className="mt-1 rounded-lg"
              />
            </div>
            {block.content.src && (
              <img src={block.content.src} alt="Preview" className="max-h-40 rounded-lg" />
            )}
            <div>
              <Label>Alt Text</Label>
              <Input
                value={block.content.alt || ''}
                onChange={(e) => updateContent('alt', e.target.value)}
                placeholder="Image description"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Caption</Label>
              <Input
                value={block.content.caption || ''}
                onChange={(e) => updateContent('caption', e.target.value)}
                placeholder="Optional caption"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Link (optional)</Label>
              <Input
                value={block.content.link || ''}
                onChange={(e) => updateContent('link', e.target.value)}
                placeholder="https://..."
                className="mt-1 rounded-lg"
              />
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div>
              <Label>Headline</Label>
              <Input
                value={block.content.headline || ''}
                onChange={(e) => updateContent('headline', e.target.value)}
                placeholder="Ready to start?"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={block.content.description || ''}
                onChange={(e) => updateContent('description', e.target.value)}
                placeholder="Join thousands of gardeners today"
                className="mt-1 rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input
                  value={block.content.button_text || ''}
                  onChange={(e) => updateContent('button_text', e.target.value)}
                  placeholder="Sign Up Free"
                  className="mt-1 rounded-lg"
                />
              </div>
              <div>
                <Label>Button Link</Label>
                <Input
                  value={block.content.button_link || ''}
                  onChange={(e) => updateContent('button_link', e.target.value)}
                  placeholder="/pricing"
                  className="mt-1 rounded-lg"
                />
              </div>
            </div>
            <ColorPicker
              label="Button Color"
              value={block.content.button_color}
              onChange={(v) => updateContent('button_color', v)}
            />
          </div>
        );

      case 'features':
        const features = block.content.features || [];
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Headline</Label>
              <Input
                value={block.content.headline || ''}
                onChange={(e) => updateContent('headline', e.target.value)}
                placeholder="Why Choose Us"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Features</Label>
              <div className="space-y-3 mt-2">
                {features.map((feature, idx) => (
                  <div key={idx} className="p-3 border rounded-lg bg-gray-50">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-2">
                        <Input
                          value={feature.icon || ''}
                          onChange={(e) => {
                            const updated = [...features];
                            updated[idx].icon = e.target.value;
                            updateContent('features', updated);
                          }}
                          placeholder="🌱"
                          className="text-center"
                        />
                      </div>
                      <div className="col-span-4">
                        <Input
                          value={feature.title || ''}
                          onChange={(e) => {
                            const updated = [...features];
                            updated[idx].title = e.target.value;
                            updateContent('features', updated);
                          }}
                          placeholder="Title"
                        />
                      </div>
                      <div className="col-span-5">
                        <Input
                          value={feature.description || ''}
                          onChange={(e) => {
                            const updated = [...features];
                            updated[idx].description = e.target.value;
                            updateContent('features', updated);
                          }}
                          placeholder="Description"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updated = features.filter((_, i) => i !== idx);
                            updateContent('features', updated);
                          }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateContent('features', [...features, { icon: '✨', title: '', description: '' }]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Feature
                </Button>
              </div>
            </div>
          </div>
        );

      case 'faq':
        const faqs = block.content.items || [];
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Headline</Label>
              <Input
                value={block.content.headline || ''}
                onChange={(e) => updateContent('headline', e.target.value)}
                placeholder="Frequently Asked Questions"
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>FAQ Items</Label>
              <div className="space-y-3 mt-2">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="p-3 border rounded-lg bg-gray-50">
                    <div className="space-y-2">
                      <Input
                        value={faq.question || ''}
                        onChange={(e) => {
                          const updated = [...faqs];
                          updated[idx].question = e.target.value;
                          updateContent('items', updated);
                        }}
                        placeholder="Question"
                      />
                      <Textarea
                        value={faq.answer || ''}
                        onChange={(e) => {
                          const updated = [...faqs];
                          updated[idx].answer = e.target.value;
                          updateContent('items', updated);
                        }}
                        placeholder="Answer"
                        rows={2}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const updated = faqs.filter((_, i) => i !== idx);
                          updateContent('items', updated);
                        }}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateContent('items', [...faqs, { question: '', answer: '' }]);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add FAQ
                </Button>
              </div>
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div>
            <Label>Height (pixels)</Label>
            <Input
              type="number"
              value={block.content.height || 40}
              onChange={(e) => updateContent('height', parseInt(e.target.value))}
              className="mt-1 rounded-lg"
            />
          </div>
        );

      case 'divider':
        return (
          <div className="space-y-4">
            <div>
              <Label>Style</Label>
              <select
                value={block.content.style || 'solid'}
                onChange={(e) => updateContent('style', e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
            </div>
            <ColorPicker
              label="Color"
              value={block.content.color}
              onChange={(v) => updateContent('color', v)}
            />
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            <div>
              <Label>Video URL (YouTube/Vimeo)</Label>
              <Input
                value={block.content.url || ''}
                onChange={(e) => updateContent('url', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="mt-1 rounded-lg"
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={block.content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                placeholder="Video title"
                className="mt-1 rounded-lg"
              />
            </div>
          </div>
        );

      case 'html':
        return (
          <div>
            <Label>Custom HTML</Label>
            <Textarea
              value={block.content.html || ''}
              onChange={(e) => updateContent('html', e.target.value)}
              placeholder="<div>Your HTML here</div>"
              className="mt-1 rounded-lg font-mono text-sm"
              rows={8}
            />
          </div>
        );

      default:
        return (
          <div className="text-gray-500 text-sm">
            Editor for {block.type} block type
          </div>
        );
    }
  };

  return (
    <div className="border rounded-xl bg-white shadow-sm overflow-hidden">
      {/* Block Header */}
      <div 
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
          <span className="text-xl">{BLOCK_ICONS[block.type] || '📦'}</span>
          <span className="font-medium">{blockDef.name || block.type}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst}>
            <ChevronUp className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast}>
            <ChevronDown className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowStyleEditor(!showStyleEditor); }}>
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
            <Copy className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            <Trash2 className="w-4 h-4" />
          </Button>
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Block Content Editor */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="p-4 border-t"
          >
            {renderContentEditor()}

            {/* Style Editor */}
            {showStyleEditor && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" /> Block Styles
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <ColorPicker
                    label="Background Color"
                    value={block.styles?.backgroundColor}
                    onChange={(v) => updateStyle('backgroundColor', v)}
                  />
                  <ColorPicker
                    label="Text Color"
                    value={block.styles?.textColor}
                    onChange={(v) => updateStyle('textColor', v)}
                  />
                  <div>
                    <Label>Padding (px)</Label>
                    <Input
                      type="number"
                      value={block.styles?.padding || ''}
                      onChange={(e) => updateStyle('padding', e.target.value)}
                      placeholder="20"
                      className="mt-1 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label>Max Width</Label>
                    <Input
                      value={block.styles?.maxWidth || ''}
                      onChange={(e) => updateStyle('maxWidth', e.target.value)}
                      placeholder="1200px"
                      className="mt-1 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Page Editor Component
const CMSPageEditor = ({ adminToken }) => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [blockTypes, setBlockTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  const [newPage, setNewPage] = useState({ title: '', slug: '' });

  useEffect(() => {
    fetchPages();
    fetchBlockTypes();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/pages`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPages(response.data.pages || []);
    } catch (error) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/cms/block-types`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setBlockTypes(response.data.block_types || {});
    } catch (error) {
      console.error("Failed to load block types");
    }
  };

  const fetchVersions = async (pageId) => {
    try {
      const response = await axios.get(`${API}/admin/cms/pages/${pageId}/versions`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setVersions(response.data.versions || []);
      setShowVersions(true);
    } catch (error) {
      toast.error("Failed to load versions");
    }
  };

  const createPage = async () => {
    if (!newPage.title || !newPage.slug) {
      toast.error("Please fill in title and slug");
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/cms/pages`, newPage, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPages([response.data, ...pages]);
      setSelectedPage(response.data);
      setShowNewPageModal(false);
      setNewPage({ title: '', slug: '' });
      toast.success("Page created!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create page");
    }
  };

  const savePage = async () => {
    if (!selectedPage) return;

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/cms/pages/${selectedPage.id}`,
        selectedPage,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSelectedPage(response.data);
      setPages(pages.map(p => p.id === response.data.id ? response.data : p));
      toast.success("Page saved!");
    } catch (error) {
      toast.error("Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (pageId) => {
    if (!confirm("Are you sure you want to delete this page?")) return;

    try {
      await axios.delete(`${API}/admin/cms/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPages(pages.filter(p => p.id !== pageId));
      if (selectedPage?.id === pageId) setSelectedPage(null);
      toast.success("Page deleted");
    } catch (error) {
      toast.error("Failed to delete page");
    }
  };

  const duplicatePage = async (pageId) => {
    try {
      const response = await axios.post(
        `${API}/admin/cms/pages/${pageId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setPages([response.data, ...pages]);
      toast.success("Page duplicated!");
    } catch (error) {
      toast.error("Failed to duplicate page");
    }
  };

  const restoreVersion = async (versionNumber) => {
    if (!selectedPage) return;

    try {
      const response = await axios.post(
        `${API}/admin/cms/pages/${selectedPage.id}/restore/${versionNumber}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSelectedPage(response.data.page);
      setShowVersions(false);
      toast.success(`Restored to version ${versionNumber}`);
    } catch (error) {
      toast.error("Failed to restore version");
    }
  };

  const addBlock = (type) => {
    if (!selectedPage) return;
    
    const blockDef = blockTypes[type];
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: blockDef?.default_content || {},
      styles: {},
      order: selectedPage.blocks?.length || 0
    };

    setSelectedPage({
      ...selectedPage,
      blocks: [...(selectedPage.blocks || []), newBlock]
    });
  };

  const updateBlock = (blockId, updatedBlock) => {
    if (!selectedPage) return;
    
    setSelectedPage({
      ...selectedPage,
      blocks: selectedPage.blocks.map(b => b.id === blockId ? updatedBlock : b)
    });
  };

  const deleteBlock = (blockId) => {
    if (!selectedPage) return;
    
    setSelectedPage({
      ...selectedPage,
      blocks: selectedPage.blocks.filter(b => b.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    if (!selectedPage) return;
    
    const blocks = [...selectedPage.blocks];
    const index = blocks.findIndex(b => b.id === blockId);
    
    if (direction === 'up' && index > 0) {
      [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
    } else if (direction === 'down' && index < blocks.length - 1) {
      [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
    }
    
    setSelectedPage({ ...selectedPage, blocks });
  };

  const duplicateBlock = (blockId) => {
    if (!selectedPage) return;
    
    const block = selectedPage.blocks.find(b => b.id === blockId);
    if (!block) return;
    
    const newBlock = {
      ...JSON.parse(JSON.stringify(block)),
      id: `block-${Date.now()}`
    };
    
    const index = selectedPage.blocks.findIndex(b => b.id === blockId);
    const blocks = [...selectedPage.blocks];
    blocks.splice(index + 1, 0, newBlock);
    
    setSelectedPage({ ...selectedPage, blocks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: colors.green.primary }} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
      {/* Sidebar - Pages List */}
      <div className="col-span-3 bg-white rounded-xl border overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between" style={{ background: colors.green.light }}>
          <h3 className="font-bold flex items-center gap-2" style={{ color: colors.green.dark }}>
            <FileText className="w-5 h-5" /> Pages
          </h3>
          <Button
            size="sm"
            onClick={() => setShowNewPageModal(true)}
            className="text-white"
            style={{ background: colors.green.primary }}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {pages.map(page => (
            <div
              key={page.id}
              className={`p-3 rounded-lg cursor-pointer transition-all ${
                selectedPage?.id === page.id 
                  ? 'bg-green-100 border-2 border-green-500' 
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
              }`}
              onClick={() => setSelectedPage(page)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{page.title}</p>
                  <p className="text-xs text-gray-500">/{page.slug}</p>
                </div>
                <Badge 
                  className="text-xs"
                  style={{
                    background: page.status === 'published' ? colors.green.light : 
                               page.status === 'scheduled' ? colors.mango.light : '#f3f4f6',
                    color: page.status === 'published' ? colors.green.dark :
                           page.status === 'scheduled' ? colors.mango.primary : '#666'
                  }}
                >
                  {page.status}
                </Badge>
              </div>
              <div className="flex gap-1 mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={(e) => { e.stopPropagation(); duplicatePage(page.id); }}
                >
                  <Copy className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-red-500"
                  onClick={(e) => { e.stopPropagation(); deletePage(page.id); }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          
          {pages.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No pages yet</p>
              <Button
                size="sm"
                className="mt-2"
                onClick={() => setShowNewPageModal(true)}
              >
                Create First Page
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Editor */}
      <div className="col-span-9 flex flex-col">
        {selectedPage ? (
          <>
            {/* Editor Header */}
            <div className="bg-white rounded-xl border p-4 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Input
                  value={selectedPage.title}
                  onChange={(e) => setSelectedPage({ ...selectedPage, title: e.target.value })}
                  className="text-lg font-bold border-0 p-0 focus:ring-0"
                  placeholder="Page Title"
                />
                <Badge>{selectedPage.blocks?.length || 0} blocks</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchVersions(selectedPage.id)}
                >
                  <History className="w-4 h-4 mr-1" /> Versions
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                  Preview
                </Button>
                <select
                  value={selectedPage.status}
                  onChange={(e) => setSelectedPage({ ...selectedPage, status: e.target.value })}
                  className="border rounded-lg px-3 py-1.5 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                </select>
                {selectedPage.status === 'scheduled' && (
                  <Input
                    type="datetime-local"
                    value={selectedPage.scheduled_publish_at?.slice(0, 16) || ''}
                    onChange={(e) => setSelectedPage({ 
                      ...selectedPage, 
                      scheduled_publish_at: new Date(e.target.value).toISOString() 
                    })}
                    className="w-48"
                  />
                )}
                <Button
                  onClick={savePage}
                  disabled={saving}
                  className="text-white"
                  style={{ background: colors.green.primary }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>

            {/* Page Settings */}
            <div className="bg-white rounded-xl border p-4 mb-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm">Slug (URL)</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-gray-400">/</span>
                    <Input
                      value={selectedPage.slug}
                      onChange={(e) => setSelectedPage({ ...selectedPage, slug: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                      className="rounded-lg"
                      placeholder="page-url"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Meta Title (SEO)</Label>
                  <Input
                    value={selectedPage.meta_title || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, meta_title: e.target.value })}
                    className="mt-1 rounded-lg"
                    placeholder="Page title for search engines"
                  />
                </div>
                <div>
                  <Label className="text-sm">Meta Description (SEO)</Label>
                  <Input
                    value={selectedPage.meta_description || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, meta_description: e.target.value })}
                    className="mt-1 rounded-lg"
                    placeholder="Brief description for search engines"
                  />
                </div>
              </div>
            </div>

            {/* Add Block Menu */}
            <div className="bg-white rounded-xl border p-4 mb-4">
              <Label className="text-sm font-medium mb-3 block">Add Block</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(blockTypes).map(([type, def]) => (
                  <Button
                    key={type}
                    variant="outline"
                    size="sm"
                    onClick={() => addBlock(type)}
                    className="rounded-lg"
                  >
                    <span className="mr-1">{def.icon}</span>
                    {def.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Blocks Editor */}
            <div className="flex-1 overflow-y-auto space-y-4 pb-4">
              {selectedPage.blocks?.length > 0 ? (
                selectedPage.blocks.map((block, index) => (
                  <BlockEditor
                    key={block.id}
                    block={block}
                    blockTypes={blockTypes}
                    onChange={(updated) => updateBlock(block.id, updated)}
                    onDelete={() => deleteBlock(block.id)}
                    onDuplicate={() => duplicateBlock(block.id)}
                    onMoveUp={() => moveBlock(block.id, 'up')}
                    onMoveDown={() => moveBlock(block.id, 'down')}
                    isFirst={index === 0}
                    isLast={index === selectedPage.blocks.length - 1}
                  />
                ))
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border">
                  <Layout className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">No blocks yet. Add your first block above!</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white rounded-xl border">
            <div className="text-center">
              <FileText className="w-20 h-20 mx-auto mb-4 text-gray-200" />
              <p className="text-gray-500 text-lg">Select a page to edit</p>
              <p className="text-gray-400 text-sm">or create a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* New Page Modal */}
      <AnimatePresence>
        {showNewPageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewPageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-4">Create New Page</h2>
              <div className="space-y-4">
                <div>
                  <Label>Page Title</Label>
                  <Input
                    value={newPage.title}
                    onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                    placeholder="About Us"
                    className="mt-1 rounded-lg"
                  />
                </div>
                <div>
                  <Label>URL Slug</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-gray-400">/</span>
                    <Input
                      value={newPage.slug}
                      onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                      placeholder="about-us"
                      className="rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowNewPageModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={createPage} className="flex-1 text-white" style={{ background: colors.green.primary }}>
                    Create Page
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Version History Modal */}
      <AnimatePresence>
        {showVersions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowVersions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5" /> Version History
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowVersions(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2">
                {versions.map(version => (
                  <div
                    key={version.id}
                    className="p-3 border rounded-lg flex items-center justify-between hover:bg-gray-50"
                  >
                    <div>
                      <p className="font-medium">Version {version.version_number}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(version.created_at).toLocaleString()}
                      </p>
                      {version.change_summary && (
                        <p className="text-sm text-gray-600 mt-1">{version.change_summary}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restoreVersion(version.version_number)}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Restore
                    </Button>
                  </div>
                ))}
                
                {versions.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No version history</p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Panel */}
      <AnimatePresence>
        {showPreview && selectedPage && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="fixed right-0 top-0 bottom-0 w-1/2 bg-white shadow-2xl z-40 overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h3 className="font-bold">Preview: {selectedPage.title}</h3>
              <Button variant="ghost" onClick={() => setShowPreview(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-8">
              {/* Render preview of blocks */}
              {selectedPage.blocks?.map(block => (
                <div 
                  key={block.id} 
                  className="mb-4"
                  style={{
                    backgroundColor: block.styles?.backgroundColor,
                    color: block.styles?.textColor,
                    padding: block.styles?.padding ? `${block.styles.padding}px` : undefined,
                    maxWidth: block.styles?.maxWidth
                  }}
                >
                  {block.type === 'hero' && (
                    <div 
                      className="rounded-xl p-12 text-center text-white"
                      style={{ backgroundColor: block.content.background_color || colors.green.primary }}
                    >
                      <h1 className="text-4xl font-bold mb-4">{block.content.headline}</h1>
                      <p className="text-xl mb-6 opacity-90">{block.content.subheadline}</p>
                      {block.content.cta_text && (
                        <button className="bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold">
                          {block.content.cta_text}
                        </button>
                      )}
                    </div>
                  )}
                  {block.type === 'text' && (
                    <div 
                      className="prose max-w-none"
                      style={{ textAlign: block.content.alignment }}
                      dangerouslySetInnerHTML={{ __html: block.content.content }}
                    />
                  )}
                  {block.type === 'image' && block.content.src && (
                    <figure>
                      <img src={block.content.src} alt={block.content.alt} className="rounded-lg max-w-full" />
                      {block.content.caption && (
                        <figcaption className="text-center text-sm text-gray-500 mt-2">{block.content.caption}</figcaption>
                      )}
                    </figure>
                  )}
                  {block.type === 'cta' && (
                    <div className="bg-gray-50 rounded-xl p-8 text-center">
                      <h2 className="text-2xl font-bold mb-2">{block.content.headline}</h2>
                      <p className="text-gray-600 mb-4">{block.content.description}</p>
                      <button 
                        className="px-6 py-3 rounded-lg font-semibold text-white"
                        style={{ backgroundColor: block.content.button_color || colors.green.primary }}
                      >
                        {block.content.button_text}
                      </button>
                    </div>
                  )}
                  {block.type === 'features' && (
                    <div>
                      <h2 className="text-2xl font-bold text-center mb-8">{block.content.headline}</h2>
                      <div className="grid grid-cols-3 gap-6">
                        {block.content.features?.map((f, i) => (
                          <div key={i} className="text-center p-4">
                            <span className="text-4xl block mb-2">{f.icon}</span>
                            <h3 className="font-semibold mb-1">{f.title}</h3>
                            <p className="text-sm text-gray-600">{f.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {block.type === 'spacer' && (
                    <div style={{ height: `${block.content.height || 40}px` }} />
                  )}
                  {block.type === 'divider' && (
                    <hr style={{ 
                      borderStyle: block.content.style || 'solid',
                      borderColor: block.content.color || '#e5e7eb'
                    }} />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CMSPageEditor;
